import React, { useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./nav.css";
import { ChevronRight } from "lucide-react";
import { GlobalContext } from "../../GlobalContext";

const Nav = () => {
  const { navigationHistory } = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { test_slug, patient_id, order_id } = useParams();
  const path = location.pathname.replace(/\/+$/, "");
  const queryParams = new URLSearchParams(location.search);
  const contactSupportOrderId = queryParams.get("order_id");

  useEffect(() => {
    console.log(
      "Nav re-evaluated at path:",
      path,
      "test_slug:",
      test_slug,
      "patient_id:",
      patient_id,
      "order_id:",
      order_id
    );
  }, [path, test_slug, patient_id, order_id]);

  const getNavItems = () => {
    const validParentRoutes = [
      "/dashboard",
      "/order-a-test",
      "/my-orders",
      "/manage-patients",
      "/help-and-support",
      "/profile",
      "/resources",
      "/order-success",
      "/verification-status",
    ];

    const pageConfig = {
      "/add-patient": { label: "Add patient", parent: "/manage-patients" },
      "/medical-intake-form": {
        label: "Medical intake form",
        parent: "/dashboard",
      },
      "/review-test-order": { label: "Review order", parent: "/order-a-test" },
      "/talk-to-specialist": { label: "Talk to a Specialist", parent: "/order-a-test" },
      "/reset-password": { label: "Reset password", parent: "/profile" },
      "/contact-form": { label: "Contact form", parent: "/help-and-support" },
      "/order-test": { label: "Requisition form", parent: "/order-a-test" },
      "/partner-labs": { label: "Our partner labs", parent: "/order-a-test" },
    };

    if (path.match(/^\/my-order\/([^\/]+)/)) {
      const prevPage = navigationHistory.slice(-2, -1)[0] || {};
      if (prevPage.path && prevPage.path.match(/^\/patient-details\/([^\/]+)/)) {
        const patientId = prevPage.path.split("/").pop();
        return [
          { label: "Manage patients", path: "/manage-patients" },
          { label: "Patient details", path: `/patient-details/${patientId}` },
          { label: "Order details", path: `/my-order/${order_id}` },
        ];
      }
      return [
        { label: "My orders", path: "/my-orders" },
        { label: "Order details", path: `/my-order/${order_id}` },
      ];
    }

    if (path.match(/^\/patient-details\/([^\/]+)/)) {
      return [
        { label: "Manage patients", path: "/manage-patients" },
        { label: "Patient details", path: `/patient-details/${patient_id}` },
      ];
    }

    if (path.match(/^\/medical-test\/([^\/]+)/)) {
      return [
        { label: "Order a test", path: "/order-a-test" },
        { label: "Test details", path: `/medical-test/${test_slug}` },
      ];
    }

    if (path === "/contact-support") {
      const prevPage = navigationHistory.slice(-2, -1)[0] || {};
      if (prevPage.path && prevPage.path.match(/^\/my-order\/([^\/]+)/)) {
        const orderId = prevPage.path.split("/").pop();
        const prevPrevPage = navigationHistory.slice(-3, -2)[0] || {};
        if (prevPrevPage.path && prevPrevPage.path.match(/^\/patient-details\/([^\/]+)/)) {
          const patientId = prevPrevPage.path.split("/").pop();
          return [
            { label: "Manage patients", path: "/manage-patients" },
            { label: "Patient details", path: `/patient-details/${patientId}` },
            { label: "Order details", path: `/my-order/${orderId}` },
            { label: "Contact support", path: `/contact-support?order_id=${orderId}` },
          ];
        }
        return [
          { label: "My orders", path: "/my-orders" },
          { label: "Order details", path: `/my-order/${orderId}` },
          { label: "Contact support", path: `/contact-support?order_id=${orderId}` },
        ];
      }
      return [
        { label: "My orders", path: "/my-orders" },
        { label: "Order details", path: "/my-orders" },
        { label: "Contact support", path: "/contact-support" },
      ];
    }

    if (path === "/review-test-order") {
      const fromOrderDetails = queryParams.get("fromOrderDetails") === "1";
      const navOrderId = queryParams.get("order_id");
      const testSlug = queryParams.get("test_slug");
      if (fromOrderDetails && navOrderId) {
        return [
          { label: "My orders", path: "/my-orders" },
          { label: "Order details", path: `/my-order/${navOrderId}` },
          { label: "Review order", path: `/review-test-order?order_id=${navOrderId}&fromOrderDetails=1` },
        ];
      }
      return [
        { label: "Order a test", path: "/order-a-test" },
        { label: "Review order", path: `/review-test-order${testSlug ? `?test_slug=${testSlug}` : ""}` },
      ];
    }

    if (path === "/talk-to-specialist") {
      return [
        { label: "Order a test", path: "/order-a-test" },
        { label: "Talk to a Specialist", path: "/talk-to-specialist" },
      ];
    }

    if (pageConfig[path]) {
      const { label, parent } = pageConfig[path];
      let parentPath = parent;
      let parentLabel = getPageLabel(parent);

      if (path === "/order-test") {
        const prevPage = navigationHistory.slice(-2, -1)[0];
        if (prevPage && prevPage.path === "/") {
          parentPath = "/order-a-test";
          parentLabel = "Order a test";
        }
      } else {
        const prevPage = navigationHistory.slice(-2, -1)[0];
        if (
          prevPage &&
          prevPage.fullPath !== path &&
          prevPage.fullPath !== parent
        ) {
          const prevPath = prevPage.path;
          if (validParentRoutes.includes(prevPath)) {
            parentPath = prevPage.fullPath;
            parentLabel = getPageLabel(prevPath, prevPage.search);
          }
        }
      }

      return [
        { label: parentLabel, path: parentPath },
        { label, path },
      ];
    }

    if (path === "/partner-labs") {
      return [
        { label: "Order a test", path: "/order-a-test" },
        { label: "Our partner labs", path: "/partner-labs" },
      ];
    }

    return [];
  };

  const getPageLabel = (path, search = "") => {
    const labels = {
      "/dashboard": "Dashboard",
      "/order-a-test": "Order a test",
      "/my-orders": "My orders",
      "/manage-patients": "Manage patients",
      "/help-and-support": "Help & support",
      "/profile": "Profile & settings",
      "/add-patient": "Add patient",
      "/medical-intake-form": "Medical intake form",
      "/review-test-order": "Review order",
      "/reset-password": "Reset password",
      "/contact-form": "Contact form",
      "/order-test": "Requisition form",
    };

    if (path.match(/^\/my-order\/([^\/]+)/)) {
      return "Order details";
    }
    if (path.match(/^\/patient-details\/([^\/]+)/)) {
      return "Patient details";
    }
    if (path.match(/^\/medical-test\/([^\/]+)/)) {
      return "Test details";
    }
    if (path === "/contact-support" && search.includes("order_id")) {
      return "Contact support";
    }
    if (path === "/contact-support") {
      return "Contact support";
    }

    return (
      labels[path] ||
      path
        .split("/")
        .pop()
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  const navItems = getNavItems();

  if (navItems.length === 0) return null;

  const containerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.2,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: { opacity: 0, x: 10, transition: { duration: 0.2 } },
  };

  const separatorVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } },
  };

  const navItemVariants = {
    hover: { scale: 1.05, transition: { duration: 0.1 } },
  };

  return (
    <motion.nav
      className="nav"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      key={path}
    >
      <AnimatePresence>
        {navItems.map((item, index) => (
          <React.Fragment key={item.path}>
            <motion.span
              className="nav-item"
              onClick={() => {
                if (item.path !== path) {
                  navigate(item.path, { state: item.state || {} }); // Pass state if defined
                }
              }}
              style={{ cursor: item.path !== path ? "pointer" : "default" }}
              variants={itemVariants}
              whileHover={item.path !== path ? "hover" : {}}
              custom={index}
              {...navItemVariants}
            >
              {item.label}
            </motion.span>
            {index < navItems.length - 1 && (
              <motion.span
                className="nav-separator"
                variants={separatorVariants}
                key={`${item.path}-separator`}
              >
                <ChevronRight />
              </motion.span>
            )}
          </React.Fragment>
        ))}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Nav;
